import type { ColorOption } from "@/types";

export interface FilterState {
  sizes: string[];
  colors: string[];
  priceRanges: string[];
  onlyNew: boolean;
  onlyBest: boolean;
  onlyAvailable: boolean;
}

export const initialFilters: FilterState = {
  sizes: [],
  colors: [],
  priceRanges: [],
  onlyNew: false,
  onlyBest: false,
  onlyAvailable: false,
};

export const priceRanges = [
  { id: "ate180", label: "Até R$ 180", min: 0, max: 180 },
  { id: "180a260", label: "R$ 180 a R$ 260", min: 180, max: 260 },
  { id: "260a330", label: "R$ 260 a R$ 330", min: 260, max: 330 },
  { id: "acima330", label: "Acima de R$ 330", min: 330, max: Infinity },
];

export const hasActiveFilters = (filters: FilterState): boolean =>
  filters.sizes.length > 0 ||
  filters.colors.length > 0 ||
  filters.priceRanges.length > 0 ||
  filters.onlyNew ||
  filters.onlyBest ||
  filters.onlyAvailable;

interface CategoryFiltersProps {
  sizes: string[];
  colors: ColorOption[];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onClear: () => void;
}

function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export default function CategoryFilters({
  sizes,
  colors,
  filters,
  onChange,
  onClear,
}: CategoryFiltersProps) {
  return (
    <div className="flex flex-col gap-7">
      <fieldset>
        <legend className="mb-3 text-[11px] uppercase tracking-[0.2em] text-foreground-500">
          Tamanho
        </legend>
        <div className="flex flex-wrap gap-2">
          {sizes.map((size) => {
            const active = filters.sizes.includes(size);
            return (
              <button
                key={size}
                type="button"
                aria-pressed={active}
                onClick={() => onChange({ ...filters, sizes: toggleValue(filters.sizes, size) })}
                className={`min-w-[3rem] cursor-pointer rounded-md border px-3 py-2 text-sm transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {size}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-3 text-[11px] uppercase tracking-[0.2em] text-foreground-500">
          Cor
        </legend>
        <div className="flex flex-col gap-2">
          {colors.map((color) => {
            const active = filters.colors.includes(color.name);
            return (
              <button
                key={color.name}
                type="button"
                aria-pressed={active}
                onClick={() => onChange({ ...filters, colors: toggleValue(filters.colors, color.name) })}
                className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-50 text-foreground-900"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                <span
                  className="h-5 w-5 shrink-0 rounded-full border border-background-300"
                  style={{ backgroundColor: color.hex }}
                />
                {color.name}
                {active ? (
                  <span className="ml-auto flex h-4 w-4 items-center justify-center">
                    <i className="ri-check-line text-primary-600" />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-3 text-[11px] uppercase tracking-[0.2em] text-foreground-500">
          Faixa de preço
        </legend>
        <div className="flex flex-col gap-2">
          {priceRanges.map((range) => {
            const active = filters.priceRanges.includes(range.id);
            return (
              <label
                key={range.id}
                className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-50 text-foreground-900"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() =>
                    onChange({
                      ...filters,
                      priceRanges: toggleValue(filters.priceRanges, range.id),
                    })
                  }
                  className="h-4 w-4 accent-primary-500"
                />
                {range.label}
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-3 text-[11px] uppercase tracking-[0.2em] text-foreground-500">
          Seleção
        </legend>
        <div className="flex flex-col gap-2">
          <label
            className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
              filters.onlyNew
                ? "border-primary-500 bg-primary-50 text-foreground-900"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            <input
              type="checkbox"
              checked={filters.onlyNew}
              onChange={() => onChange({ ...filters, onlyNew: !filters.onlyNew })}
              className="h-4 w-4 accent-primary-500"
            />
            Só novidades
          </label>
          <label
            className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
              filters.onlyBest
                ? "border-primary-500 bg-primary-50 text-foreground-900"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            <input
              type="checkbox"
              checked={filters.onlyBest}
              onChange={() => onChange({ ...filters, onlyBest: !filters.onlyBest })}
              className="h-4 w-4 accent-primary-500"
            />
            Mais vendidos
          </label>
          <label
            className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
              filters.onlyAvailable
                ? "border-primary-500 bg-primary-50 text-foreground-900"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            <input
              type="checkbox"
              checked={filters.onlyAvailable}
              onChange={() => onChange({ ...filters, onlyAvailable: !filters.onlyAvailable })}
              className="h-4 w-4 accent-primary-500"
            />
            Disponível agora
          </label>
        </div>
      </fieldset>

      <button
        type="button"
        onClick={onClear}
        className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200"
      >
        <i className="ri-filter-off-line text-base" />
        Limpar filtros
      </button>
    </div>
  );
}